export class AnimationManager {
  constructor() {
    this.isAnimating = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.performanceMode = false;
  }

  /**
   * Animate category transition with smooth fade and slide effects
   */
  async animateCategoryTransition(oldContent, newContent, direction = 'fade') {
    if (this.isAnimating || this.reducedMotion) {
      this.immediateTransition(oldContent, newContent);
      return;
    }

    this.isAnimating = true;

    try {
      if (direction === 'slide') {
        await this.slideTransition(oldContent, newContent);
      } else {
        await this.fadeTransition(oldContent, newContent);
      }
    } finally {
      this.isAnimating = false;
    }
  }

  /**
   * Smooth fade transition between content
   */
  async fadeTransition(oldContent, newContent) {
    if (oldContent) {
      oldContent.classList.add('fade-out');
      await this.wait(150);
    }

    if (newContent) {
      newContent.style.opacity = '0';
      newContent.style.transform = 'translateY(10px)';
    }

    if (newContent) {
      requestAnimationFrame(() => {
        newContent.classList.add('fade-in');
        newContent.style.opacity = '';
        newContent.style.transform = '';
      });
    }

    if (oldContent) {
      oldContent.classList.remove('fade-out');
    }
  }

  /**
   * Slide transition with direction support
   */
  async slideTransition(oldContent, newContent) {
    if (oldContent) {
      oldContent.classList.add('content-slide-exit');
      await this.wait(150);
    }

    if (newContent) {
      newContent.classList.add('content-slide-enter');
    }

    setTimeout(() => {
      if (oldContent) {
        oldContent.classList.remove('content-slide-exit');
      }
      if (newContent) {
        newContent.classList.remove('content-slide-enter');
      }
    }, 300);
  }

  /**
   * Animate menu items with staggered entrance
   */
  animateMenuItems(container, delay = 50) {
    if (this.reducedMotion) return;

    const items = container.querySelectorAll('.menu-item-card');

    items.forEach(item => {
      item.classList.remove('animate-in');
      item.style.opacity = '0';
      item.style.transform = 'translateY(20px)';
    });

    items.forEach((item, index) => {
      setTimeout(() => {
        item.classList.add('animate-in');
        item.style.opacity = '';
        item.style.transform = '';
      }, index * delay);
    });
  }

  /**
   * Animate category selection with pulse effect
   */
  animateCategorySelection(element) {
    if (this.reducedMotion) return;

    element.classList.add('pulse');
    setTimeout(() => {
      element.classList.remove('pulse');
    }, 300);
  }

  /**
   * Animate section title update
   */
  animateSectionTitle(element, newText) {
    if (this.reducedMotion) {
      element.textContent = newText;
      return;
    }

    element.style.opacity = '0';
    element.style.transform = 'translateX(-15px)';

    setTimeout(() => {
      element.textContent = newText;
      element.classList.add('updating');
      element.style.opacity = '';
      element.style.transform = '';

      setTimeout(() => {
        element.classList.remove('updating');
      }, 250);
    }, 100);
  }


  /**
   * Smooth scroll to top of content area
   */
  smoothScrollToTop(element) {
    if (this.reducedMotion) {
      element.scrollTop = 0;
      return;
    }

    element.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  /**
   * Animate button press with spring effect
   */
  animateButtonPress(button) {
    if (this.reducedMotion) return;

    button.style.transform = 'scale(0.95)';
    setTimeout(() => {
      button.style.transform = '';
    }, 150);
  }

  /**
   * Cross-fade between two elements
   */
  async crossFade(elementOut, elementIn, duration = 400) {
    if (this.reducedMotion) {
      if (elementOut) elementOut.style.display = 'none';
      if (elementIn) elementIn.style.display = 'block';
      return;
    }

    const halfDuration = duration / 2;

    if (elementOut) {
      elementOut.style.transition = `opacity ${halfDuration}ms ease-out`;
      elementOut.style.opacity = '0';

      setTimeout(() => {
        elementOut.style.display = 'none';
      }, halfDuration);
    }

    if (elementIn) {
      setTimeout(() => {
        elementIn.style.display = 'block';
        elementIn.style.opacity = '0';
        elementIn.style.transition = `opacity ${halfDuration}ms ease-in`;

        requestAnimationFrame(() => {
          elementIn.style.opacity = '1';
        });
      }, halfDuration);
    }

    await this.wait(duration);
  }

  /**
   * Add spring bounce animation to element
   */
  addSpringBounce(element) {
    if (this.reducedMotion) return;

    element.classList.add('bounce-in');
    setTimeout(() => {
      element.classList.remove('bounce-in');
    }, 600);
  }

  /**
   * Enable performance mode (reduced animations)
   */
  enablePerformanceMode() {
    this.performanceMode = true;
    document.body.classList.add('performance-mode');
  }

  /**
   * Disable performance mode
   */
  disablePerformanceMode() {
    this.performanceMode = false;
    document.body.classList.remove('performance-mode');
  }

  /**
   * Immediate transition without animation
   */
  immediateTransition(oldContent, newContent) {
    if (oldContent) {
      oldContent.style.display = 'none';
    }
    if (newContent) {
      newContent.style.display = 'block';
      newContent.style.opacity = '1';
      newContent.style.transform = 'none';
    }
  }

  /**
   * Wait for specified duration
   */
  wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if device supports smooth animations
   */
  supportsAnimations() {
    return !this.reducedMotion && !this.performanceMode;
  }

  /**
   * Animate number counting up (for cart count, etc.)
   */
  animateCounter(element, from, to, duration = 500) {
    if (this.reducedMotion) {
      element.textContent = to;
      return;
    }

    const startTime = performance.now();
    const difference = to - from;

    const updateCounter = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(from + (difference * easeOut));

      element.textContent = current;

      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      }
    };

    requestAnimationFrame(updateCounter);
  }
}